import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import SplitText from '../components/SplitText';
import CircularGallery from '../components/CircularGallery';
// import DispatchProcessSection from '../components/DispatchProcessSection';

/* ─── Google Fonts ─── */
const interFontLink = document.createElement('link');
interFontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
interFontLink.rel = 'stylesheet';
document.head.appendChild(interFontLink);

const playfairLink = document.createElement('link');
playfairLink.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;1,700&display=swap';
playfairLink.rel = 'stylesheet';
document.head.appendChild(playfairLink);

/* ─── Scroll-reveal wrapper ─── */
const Reveal = ({ children, delay = 0, direction = 'up', className = '', style = {} }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const variants = {
    hidden: {
      opacity: 0,
      y: direction === 'up' ? 40 : direction === 'down' ? -40 : 0,
      x: direction === 'left' ? 40 : direction === 'right' ? -40 : 0,
    },
    visible: { opacity: 1, y: 0, x: 0 },
  };
  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={variants}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

/* ══════════════════════════════════════════════
    HERO FULLSCREEN SLIDER
══════════════════════════════════════════════ */
const SLIDES = [
  {
    bg: '/Images/blackpanther slide 2.png',
    eyebrow: 'Helmet Decals',
    headline: ['Wakanda', 'Forever'],
    sub: 'Licensed-style helmet graphics with deep-violet UV printing — character-grade detail on every curve.',
    accent: '#7c3aed',
    cta: 'Shop Decals',
  },
  {
    bg: '/Images/trance 1.png',
    eyebrow: 'Full-Wrap Graphics',
    headline: ['Day of the', 'Dead Series'],
    sub: 'Full-wrap sugar-skull graphics with copper and charcoal tones — intricate art that rides with you.',
    accent: '#c97640',
    cta: 'Explore Range',
  },
  {
    bg: '/Images/cricket helmet (3).png',
    eyebrow: 'Helmet Decals',
    headline: ['Wakanda', 'Forever'],
    sub: 'Licensed-style helmet graphics with deep-violet UV printing — character-grade detail on every curve.',
    accent: '#7c3aed',
    cta: 'Shop Decals',
  },
  {
    bg: '/Images/i5 vehicle.png',
    eyebrow: 'Full-Wrap Graphics',
    headline: ['Day of the', 'Dead Series'],
    sub: 'Full-wrap sugar-skull graphics with copper and charcoal tones — intricate art that rides with you.',
    accent: '#c97640',
    cta: 'Explore Range',
  },
];

const SLIDE_DURATION = 6000;

/* ─── Hero slide image ─── */
const HeroSlideImage = ({ src }) => (
  <motion.img
    src={src}
    alt=""
    initial={{ scale: 1.05, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ scale: 1.02, opacity: 0 }}
    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
    style={{
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
      display: 'block',
    }}
  />
);



/* ─── Hero Slider ─── */
const HeroSlider = () => {
  const [cur, setCur] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCur(c => (c + 1) % SLIDES.length);
      setAnimKey(k => k + 1);
    }, SLIDE_DURATION);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const slide = SLIDES[cur];
  const accent = slide.accent;

  return (
    <section
      style={{
        position: 'relative',
        width: '100%',
        height: 'calc(100svh - 90px)',
        minHeight: '520px',
        maxHeight: '800px',
        overflow: 'hidden',
        fontFamily: "'Inter', sans-serif",
        zIndex: 1,
      }}
    >
      {/* Full-bleed image */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <AnimatePresence>
          <HeroSlideImage key={cur} src={slide.bg} />
        </AnimatePresence>
      </div>

      {/* Gradient overlays */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 35%)',
      }} />

      {/* Accent colour wash */}
      <AnimatePresence>
        <motion.div
          key={`wash-${cur}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4 }}
          style={{
            position: 'absolute', inset: 0, zIndex: 1,
            background: `radial-gradient(ellipse at 50% 70%, ${accent}18 0%, transparent 60%)`,
            pointerEvents: 'none',
          }}
        />
      </AnimatePresence>
    </section>
  );
};

/* ─── Capability card ─── */
const CapCard = ({ src, alt, label, desc, delay }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Reveal delay={delay} direction="up">
      <Link
        to="/capabilities"
        style={{ textDecoration: 'none', display: 'block', color: 'inherit' }}
        onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}
      >
      <motion.div
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        animate={{
          y: hovered ? -5 : 0,
          boxShadow: hovered
            ? '0 20px 48px rgba(0,0,0,0.16), 0 4px 12px rgba(0,0,0,0.08)'
            : '0 4px 20px rgba(0,0,0,0.09)',
        }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: '4px',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <motion.div
          animate={{ scaleX: hovered ? 1 : 0 }}
          initial={{ scaleX: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: '#cc0000', transformOrigin: 'left', zIndex: 3,
          }}
        />

        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <motion.img
            src={src}
            alt={alt}
            animate={{ scale: hovered ? 1.07 : 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>

        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: hovered
            ? 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.3) 100%)'
            : 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)',
          transition: 'background 0.35s ease',
        }} />

        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '16px 18px', zIndex: 2,
          opacity: hovered ? 0 : 1,
          transform: hovered ? 'translateY(6px)' : 'translateY(0)',
          transition: 'opacity 0.28s ease, transform 0.28s ease',
        }}>
          <p style={{
            margin: 0, color: '#fff', fontWeight: 700, fontSize: '15px',
            letterSpacing: '0.2px', textShadow: '0 1px 6px rgba(0,0,0,0.45)',
          }}>{label}</p>
        </div>

        <motion.div
          initial={false}
          animate={{ opacity: hovered ? 1 : 0, y: hovered ? 0 : 12 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: 'absolute', inset: 0, zIndex: 2,
            display: 'flex', flexDirection: 'column',
            justifyContent: 'center', alignItems: 'center',
            padding: '22px 20px', textAlign: 'center', pointerEvents: 'none',
          }}
        >
          <p style={{
            color: '#ff4d4d', fontWeight: 700, fontSize: '11px',
            marginBottom: '10px', letterSpacing: '1.8px', textTransform: 'uppercase',
          }}>{label}</p>
          <p style={{
            color: '#f2f2f2', fontSize: '13px', lineHeight: 1.65,
            margin: 0, maxWidth: '260px',
          }}>{desc}</p>
        </motion.div>
      </motion.div>
      </Link>
    </Reveal>
  );
};

/* ─── Why Choose items ─── */
const WHY_ITEMS = [
  {
    icon: (<svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>),
    label: 'Premium Quality Products',
    desc: 'Manufactured using high-grade materials for durability and performance.',
  },
  {
    icon: (<svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>),
    label: 'In-House Design & Manufacturing',
    desc: 'Complete control from concept to final production.',
  },
  {
    icon: (<svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>),
    label: 'Customized Solutions',
    desc: 'Labels, decals, and graphics tailored to your exact requirements.',
  },
  {
    icon: (<svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/></svg>),
    label: 'Strict Quality Control',
    desc: 'Every product undergoes thorough inspection before delivery.',
  },
  {
    icon: (<svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>),
    label: 'Timely Delivery',
    desc: 'Efficient production processes ensure on-time project completion.',
  },
  {
    icon: (<svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
    label: 'Industry Expertise',
    desc: 'Serving diverse industries with reliable branding solutions since 2017.',
  },
];

const WhyCard = ({ icon, label, desc, delay }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Reveal delay={delay} direction="up" style={{ height: '100%' }}>
      <div style={{ height: '100%' }}>
        <motion.div
          onHoverStart={() => setHovered(true)}
          onHoverEnd={() => setHovered(false)}
          animate={{
            y: hovered ? -4 : 0,
            boxShadow: hovered
              ? '0 16px 40px rgba(204,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)'
              : '0 2px 14px rgba(0,0,0,0.06)',
          }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{
            height: '100%',
            minHeight: '190px',
            background: '#ffffff',
            borderRadius: '4px',
            padding: '30px 28px 28px',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'default',
            border: '1px solid #e8e8e8',
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'left',
          }}
        >
          <motion.div
            animate={{ scaleX: hovered ? 1 : 0 }}
            initial={{ scaleX: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
              background: '#cc0000', transformOrigin: 'left', zIndex: 2,
            }}
          />

          <motion.div
            animate={{ opacity: hovered ? 0.14 : 0.07, color: hovered ? '#cc0000' : '#999' }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              right: '-36px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              lineHeight: 0,
              zIndex: 0,
            }}
          >
            {icon}
          </motion.div>

          <div style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: '10px',
            maxWidth: '78%',
          }}>
            <p style={{
              margin: 0,
              fontWeight: 700,
              fontSize: '16px',
              color: '#1a1a1a',
              letterSpacing: '-0.2px',
              lineHeight: 1.35,
              minHeight: '48px',
              display: 'flex',
              alignItems: 'flex-start',
            }}>{label}</p>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#666',
              lineHeight: 1.65,
              flex: 1,
            }}>{desc}</p>
          </div>
        </motion.div>
      </div>
    </Reveal>
  );
};

/* ══════════════════════════════════════════════
    HOMESCREEN
══════════════════════════════════════════════ */
const Homescreen = () => {
  return (
    <div style={{ backgroundColor: '#fff8fadb', minHeight: '100vh', overflowX: 'hidden', fontFamily: "'Inter', sans-serif" }}>

      {/* ── NEW FULLSCREEN HERO ── */}
      <HeroSlider />

      {/* Company intro */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          backgroundColor: '#fff8fadb',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '32px 60px 40px',
        }}
      >
        <SplitText text="Decal Tech Private Limited" tag="h1" duration={1.5} delay={80} splitType="chars" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginTop: '50px', maxWidth: '860px', fontSize: '17px', lineHeight: '1.8', color: '#333' }}
        >
          <p style={{ marginBottom: '25px' }}>
            Established in 2017, Decal Tech Private Limited is a leading manufacturer of premium self-adhesive
            labels, automotive graphics, and water-slide decals, delivering reliable branding and identification
            solutions across diverse industries. With a strong focus on quality, innovation, and precision, we
            combine advanced manufacturing technologies with industry expertise to create durable, high-performance
            products that meet the evolving needs of modern businesses.
          </p>
          <p style={{ marginBottom: '0' }}>
            Driven by a commitment to quality and innovation, we combine advanced manufacturing technology,
            precision engineering, and industry expertise to produce durable, high-performance products.
            Our focus on consistency, attention to detail, and customer satisfaction enables us to deliver
            solutions that enhance brand visibility, product identification, and overall visual appeal.
          </p>
        </motion.div>
      </div>

      {/* Our Products */}
      <div style={{ padding: '32px 60px 20px', textAlign: 'center', marginTop: '12px' }}>
        <h4 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '6px', marginTop: 0, letterSpacing: '-0.5px', color: '#111' }}>Our Products</h4>
        <div style={{
          width: '100vw', height: '500px', position: 'relative',
          overflow: 'hidden', marginLeft: 'calc(-50vw + 50%)',
        }}>
          <CircularGallery bend={1} scrollSpeed={2} textColor="#222222" font="bold 34px Figtree" borderRadius={0.015} />
        </div>
        <Button
          href="/products"
          style={{
            backgroundColor: '#cc0000', border: 'none', borderRadius: '2px',
            padding: '12px 32px', fontWeight: 600, letterSpacing: '1.5px',
            fontSize: '13px', textTransform: 'uppercase',
            boxShadow: '0 4px 20px rgba(204,0,0,0.35)',
            transition: 'transform 0.2s, box-shadow 0.2s', marginTop: '8px',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(204,0,0,0.45)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(204,0,0,0.35)'; }}
        >
          Explore Products
        </Button>
      </div>

      {/* Our Capabilities */}
      <div style={{
        padding: '40px 60px 44px', textAlign: 'center',
        background: 'linear-gradient(180deg, #fff8fadb 0%, #f2f2f2 100%)',
      }}>
        <Reveal direction="up">
          <h3 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.5px', color: '#111' }}>Our Capabilities</h3>
          <p style={{ fontSize: '15px', color: '#666', maxWidth: '520px', margin: '0 auto 28px' }}>
            From design to dispatch — everything under one roof.
          </p>
        </Reveal>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))',
          gap: '18px',
          maxWidth: '1140px',
          margin: '0 auto',
        }}>
          <CapCard src="/Images/C1.jpeg" alt="Graphic Designing" label="Graphic Designing" desc="In-house artwork creation using professional tools for precise, print-ready files." delay={0} />
          <CapCard src="/Images/C2.png" alt="Manufacturing" label="Manufacturing" desc="Advanced UV & screen printing presses for high-volume, colour-accurate production." delay={0.1} />
          <CapCard src="/Images/C3.png" alt="Quality Control Lab" label="Quality Control Lab" desc="Spectrophotometers and adhesion testing ensure every batch meets specification." delay={0.2} />
          <CapCard src="/Images/product9.jpeg" alt="Screen Printing" label="Screen Printing" desc="High-opacity, vibrant screen printing for durable, large-run label production." delay={0.3} />
          <CapCard src="/Images/product8.jpeg" alt="Digital Printing" label="Digital Printing" desc="Full-colour digital printing for short runs, prototypes, and variable data labels." delay={0.4} />
          <CapCard src="/Images/C3.jpeg" alt="Package & Dispatch" label="Package & Dispatch" desc="Securely packed, labeled, and dispatched for safe, on-time delivery." delay={0.5} />
        </div>
      </div>

      {/* Why Choose Decal Tech */}
      <div style={{ padding: '40px 60px 48px', textAlign: 'center', background: '#ffffff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-120px', right: '-120px', width: '420px', height: '420px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(204,0,0,0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '320px', height: '320px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(204,0,0,0.03) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Reveal direction="up">
          <h3 style={{ fontSize: '2rem', fontWeight: 700, color: '#111', letterSpacing: '-0.5px', margin: '0 0 28px' }}>Why Choose Decal Tech?</h3>
        </Reveal>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))',
          gap: '18px',
          maxWidth: '1140px',
          margin: '0 auto',
          alignItems: 'stretch',
        }}>
          {WHY_ITEMS.map((item, i) => (
            <WhyCard key={item.label} icon={item.icon} label={item.label} desc={item.desc} delay={i * 0.08} />
          ))}
        </div>
      </div>

      {/* From Intake to Dispatch */}
      {/* <DispatchProcessSection /> */}

    </div>
  );
};

export default Homescreen;